import {
  Banner,
  PasswordInput,
  PrimaryButton,
} from "@brexhq/component-library";
import { useTheme } from "@emotion/react";
import styled from "@emotion/styled";
import { margin } from "polished";
import * as React from "react";

import { useFormSubmissionContext } from "@brexhq/next/src/components/modules/LeadForm/FormSubmissionProvider";
import { Form } from "@brexhq/next/src/components/modules/LeadForm/Steps/elements";
import { LeadFormSteps } from "@brexhq/next/src/components/modules/LeadForm/machines/types";
import {
  LeadFormValues,
  StepProps,
} from "@brexhq/next/src/components/modules/LeadForm/sharedTypes";
import { NAV_HEIGHT } from "@brexhq/next/src/components/navigation/consts";
import { UnstyledLink } from "@brexhq/next/src/components/shared/Link/UnstyledLink";
import { Video } from "@brexhq/next/src/components/shared/Video/video";
import { loadIovation } from "@brexhq/next/src/utils/analytics";
import { Text } from "@brexhq/shared/components/Typography/Text";

import { useLeadFormModuleContext } from "../../LeadFormModuleProvider";
import { FinalStateViewTemplate } from "../PageTemplates/FinalStateViewTemplate";
import {
  ErrorBannerWrapper,
  TakeoverContent,
} from "../PageTemplates/PageTemplate.elements";

const Link = styled(UnstyledLink)(({ theme }) => ({
  color: theme.colors.accent.primary.background.default,
}));

const PlatformAgreementWrapper = styled.div(({ theme }) => ({
  ...margin(theme.spacing.m32, 0, 0),
  ...theme.typography.DetailLargeR,
  textWrap: "pretty",
  "> label": {
    alignItems: "flex-start",
  },
}));

const CenterAlign = styled.div({
  textAlign: "center",
});

const FormContent = styled.div(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing[28],
}));

const CenteredVideo = styled(Video)({
  width: "100%",
  maxWidth: "350px",
  height: "fit-content",
  marginLeft: "auto",
  marginRight: "auto",
});

export const Password: React.FC<StepProps> = () => {
  const {
    submitError,
    stateMachineCtx,
    submit,
    form: { fields, setProgress, getValues },
  } = useFormSubmissionContext();
  const { formSettings } = useLeadFormModuleContext();
  const validFields = !!getValues("password") && !fields.password.error;
  const { colors } = useTheme();

  React.useEffect(() => {
    loadIovation();
    setProgress(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSubmit = React.useCallback(
    async (event) => {
      /**
       * List of fields used for this form. Most will have 1 field.
       * Type safe so the string fieldNames are based off FormValues
       */
      const fieldList: (keyof LeadFormValues)[] = ["password"];
      /**
       * Handles form submission including validations.
       * Reference FormSubmissionProvider.tsx for more context
       */
      submit({
        event,
        fields: fieldList,
      });
    },
    [submit],
  );
  const error = submitError || stateMachineCtx.errorMessage;

  return (
    <FinalStateViewTemplate currentLeadFormStep={LeadFormSteps.password}>
      <TakeoverContent>
        <Form
          onSubmit={(e) => {
            e.preventDefault();
            onSubmit(e);
          }}
          style={
            // if showing border, we dont want content cutoff, add 20px for padding from nav
            formSettings?.hasBorder ? { marginTop: `${NAV_HEIGHT + 20}px` } : {}
          }
          padding={formSettings?.padding}
          hasBorder={formSettings?.hasBorder}
          hasShadow={formSettings?.hasShadow}
          backgroundColor={formSettings?.backgroundColor}
        >
          <CenteredVideo
            src="https://brand.brex.com/m/2dbccd07ab48f40b/2C323D44-congratsmotion-whitebg.mp4"
            autoPlay
            loop
            muted
          />
          <CenterAlign>
            <Text
              as="h2"
              className="_lr-hide dd-privacy-mask"
              color={colors.content.primary}
              components={{
                LRG: "Headline3",
                MED: "Headline3",
                SML: "Headline4",
              }}
            >
              It’s a match! Let’s create your account
            </Text>
          </CenterAlign>
          <CenterAlign>
            <Text
              as="p"
              className="_lr-hide dd-privacy-mask"
              color={colors.content.primary}
              components={{
                LRG: "Body1",
                MED: "Body1",
                SML: "Body2",
              }}
            >
              Enter a secure password to create your account.
            </Text>
          </CenterAlign>
          <FormContent>
            <PasswordInput
              inputMode="text"
              size="large"
              {...fields.password}
              label={undefined}
            />
            <PrimaryButton isFullWidth disabled={!validFields} type="submit">
              Submit
            </PrimaryButton>
            {error && (
              <ErrorBannerWrapper>
                <Banner layout="compact" variant="error" title={error.title}>
                  {error.message}
                </Banner>
              </ErrorBannerWrapper>
            )}
          </FormContent>
        </Form>
        <PlatformAgreementWrapper>
          By clicking submit, I acknowledge and agree to the terms of the{" "}
          <Link to="/legal" target="_blank">
            Brex Platform Agreement
          </Link>{" "}
          on behalf of the Company identified above, that I am authorized to do
          so on behalf of the Company, and that I have reviewed the terms of the{" "}
          <Link to="/privacy/" target="_blank">
            Brex Privacy Policy
          </Link>
          .
        </PlatformAgreementWrapper>
      </TakeoverContent>
    </FinalStateViewTemplate>
  );
};
